/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { useEffect } from 'react';
import { PlusCircle, Search, Trash } from '@jda/lui-common-icon-library-mui5';
import { Box, Button, Divider, Grid, InputAdornment, List, ListItem, ListItemText, Paper, TextField, Tooltip } from '@mui/material';

import {
  FONT_SIZES,
  CUSTOMTRANSFERLIST,
  AVAILABLE_MODULE_TEXT,
  SELECTED_MODULE_TEXT,
  DISPLAY_EMPTY_SUBHEADING_LEFT_SIDE,
  DISPLAY_EMPTY_SUBHEADING_RIGHT_SIDE,
  ADD_ENTITLEMENT_FROM_ACCOUNT,
  SUBSCRIBED_MODULES
} from './constants';
import { TransferListStyle } from './styles';
import { IModule } from '../../models/Realms/RealmContracts';
import EmptyComponent from '../../shared/emptyComponent/EmptyComponent';

export const removeHighlightedChar = (nodesArray: any, className: string) => {
  nodesArray.forEach((el: any) => {
    if (el.classList.contains(`${className}`)) {
      el.classList.remove(className);
    }
    if (el.children[0] && el.children[0].classList.contains(`${className}`)) {
      el.children[0].classList.remove(className);
    }
  });
};

export const addHighlightForMatchChar = (nodesArray: any, term: string, _className: string) => {
  let nodesText = nodesArray[0].textContent;
  let startIndex = nodesText.toLowerCase().indexOf(term);
  let rangeOfMatchChar = nodesText.substr(startIndex, term.length);
  nodesArray[0].textContent = nodesText.replace(rangeOfMatchChar, `${rangeOfMatchChar}`);
};

const not = (a: Array<any>, b: Array<any>) => {
  return a.filter(value => b.indexOf(value) === -1);
};

const intersection = (a: Array<any>, b: Array<any>) => {
  return a.filter(value => b.indexOf(value) !== -1);
};

export interface IListFormat {
  itemLabel: string;
  value: string;
  attributeCount?: number;
  documentation: string;
  group?: string;
}

interface ITransferListProps {
  intlData?: any;
  classes?: any;
  labelId: string;
  dataAvailable: Array<IModule>;
  dataInView: Array<IModule>;
  leftTitle: string;
  rightTitle: string;
  onSubmit: (itemInView: Array<IModule>, itemAvailable: Array<IModule>) => void;
  isRealmEntitlement?: boolean;
}
const CustomTransferList = (props: ITransferListProps) => {
  const {
    addClearText,
    entityListSearch,
    entityLabel,
    mrT30P,
    title,
    paper,
    root,
    leftPanel,
    rightPanel,
    button,
    mrgnLeftM14P,
    labelDisabled,
    filteredEntityList,
    width400,
    highlight
  } = TransferListStyle();

  const {
    labelId,
    leftTitle,
    rightTitle,
    dataAvailable,
    dataInView,
    onSubmit,
    isRealmEntitlement = false,
  } = props;
  const checkedArray: any = [];
  let rightArray: any = dataInView || [];
  let leftArray: any = dataAvailable || [];
  const [checked, setChecked] = React.useState(checkedArray);
  const [left, setLeft] = React.useState(leftArray);
  const [right, setRight] = React.useState(rightArray);
  const [leftModuleListSearchValue, setLeftModuleListSearchValue] = React.useState('');
  const [rightModuleListSearchValue, setRightModuleListSearchValue] = React.useState('');

  useEffect(() => {
    setLeft(dataAvailable);
    if (labelId === CUSTOMTRANSFERLIST.MODULES) {
      setChecked([]);
    }
  }, [dataAvailable, labelId]);

  useEffect(() => {
    setRight(dataInView);
  }, [dataInView]);

  const leftChecked: Array<any> = intersection(checked, left);
  const rightChecked: Array<any> = intersection(checked, right);

  const handleClearAll = (_items: any) => () => {
    setLeft(left.concat(right));
    setRight([]);
    onSubmit(rightChecked, left.concat(right));
    setRightModuleListSearchValue('');
  };

  const handleAddAll = (_items: any) => () => {
    setRight(right.concat(left));
    setLeft([]);
    onSubmit(left.concat(right), rightChecked);
    setLeftModuleListSearchValue('');
  };

  const handleCheckedRight = (_e: any, item: any) => {
    setLeftModuleListSearchValue('');
    setRightModuleListSearchValue('');
    leftChecked.push(item);
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    onSubmit(right.concat(leftChecked), not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = (_e: any, item: any) => {
    setLeftModuleListSearchValue('');
    setRightModuleListSearchValue('');
    rightChecked.push(item);
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    onSubmit(not(right, rightChecked), left.concat(rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const searchModuleList = (event: any, boxName: string) => {
    let term = event.target.value.toLowerCase();
    let entityListArray;
    const leftBoxContainer = document.getElementById('modules_left');
    const leftEntityListArray =
      leftBoxContainer && Array.from(leftBoxContainer.getElementsByClassName('listContainer'));

    const rightBoxContainer = document.getElementById('modules_right');
    const rightEntityListArray =
      rightBoxContainer && Array.from(rightBoxContainer.getElementsByClassName('listContainer'));
    if (boxName === CUSTOMTRANSFERLIST.LEFTBOX) {
      setLeftModuleListSearchValue(term);
      entityListArray = leftEntityListArray;
    } else {
      setRightModuleListSearchValue(term);
      entityListArray = rightEntityListArray;
    }
    entityListArray?.forEach((el: any) => {
      const entitiesNamesArray: any = Array.from(el.getElementsByClassName('MuiListItemText-primary'));
      let entitiesDiv = el.classList;
      const headerText = entitiesNamesArray[0].textContent.toLowerCase();
      if (term === '') {
        removeHighlightedChar(entitiesNamesArray, highlight);
        entitiesDiv.remove(`${filteredEntityList}`);
      } else if (!headerText.includes(term)) {
        entitiesDiv.add(`${filteredEntityList}`);
      } else {
        addHighlightForMatchChar(entitiesNamesArray, term, highlight);
        entitiesDiv.remove(`${filteredEntityList}`);
      }
    });
  };

  const displayNoEntityScreen = (subHeading: string) => {
    return (
      <EmptyComponent
        disableCreateAction
        subTitle={subHeading}
        title={`It's Empty here`}
      />
    );
  };

  const customList = (items: any, box: string, label: string, _titleLabel: string, clearAll: boolean) => (
    <div>
      <span className={`${title}`}>{label}</span>
      {clearAll ? (
        <span id='remove-all-entitlements' className={`${addClearText} ${right.length === 0 ? labelDisabled : ''}`} onClick={handleClearAll(items)}>
          Remove All
        </span>
      ) : (
        <span id='add-all-entitlements' className={`${addClearText} ${left.length === 0 ? labelDisabled : ''}`} onClick={handleAddAll(items)}>
          Add All
        </span>
      )}
      <Paper className={paper}>
        {items.length > 0 ? (
          <Box id="searchEntitylist">
            {box === CUSTOMTRANSFERLIST.LEFTBOX ? (
              <TextField
                id="entityListSearch"
                className={entityListSearch}
                onChange={event => searchModuleList(event, 'leftBox')}
                placeholder="Search"
                value={leftModuleListSearchValue}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="disabled" id="create-view-seacrch-icon" fontSize={FONT_SIZES.SMALL} />
                    </InputAdornment>
                  )
                }}
              />
            ) : (
              <TextField
                id="entityListSearch"
                className={entityListSearch}
                onChange={event => searchModuleList(event, 'rightBox')}
                placeholder="Search"
                value={rightModuleListSearchValue}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="disabled" id="create-view-seacrch-icon" fontSize={FONT_SIZES.SMALL} />
                    </InputAdornment>
                  )
                }}
              />
            )}
          </Box>
        ) : box === CUSTOMTRANSFERLIST.LEFTBOX ? (
          <div>{displayNoEntityScreen(isRealmEntitlement ? ADD_ENTITLEMENT_FROM_ACCOUNT : DISPLAY_EMPTY_SUBHEADING_LEFT_SIDE)}</div>
        ) : (
          <div>{displayNoEntityScreen(DISPLAY_EMPTY_SUBHEADING_RIGHT_SIDE)}</div>
        )}
        {items.length > 0 ? (
          <List dense component="div" role="list">
            {items?.map((item: any, _index: any) => {
              const { id, name, appName } = item;
              return (
                <div key={id} className={'listContainer'}>
                  <Divider />
                  <ListItem role="listitem">
                    <Tooltip
                      arrow
                      placement="top"
                      classes={{ tooltip: width400 }}
                      title={item.title || ''}
                      aria-label="simple"
                    >
                      <ListItemText className={`${mrgnLeftM14P} ${entityLabel}`} primary={name} secondary={appName} />
                    </Tooltip>
                    {box === CUSTOMTRANSFERLIST.LEFTBOX ? (
                      // <Tooltip title="add">
                      <Button
                        size={FONT_SIZES.LARGE}
                        className={button}
                        id={`${labelId}_btnRight`}
                        onClick={e => handleCheckedRight(e, item)}
                      >
                        <PlusCircle />
                      </Button>
                      // </Tooltip>
                    ) : (
                      // <Tooltip title="delete">
                      <Button
                        size={FONT_SIZES.LARGE}
                        className={button}
                        id={`${labelId}_btnLeft`}
                        onClick={e => handleCheckedLeft(e, item)}
                      >
                        <Trash />
                      </Button>
                      // </Tooltip>
                    )}
                  </ListItem>
                </div>
              );
            })}
            <ListItem />
          </List>
        ) : null}
      </Paper>
    </div>
  );

  return (
    <Grid container alignItems="center" className={root}>
      <Grid id={`${labelId}_left`} className={leftPanel} item style={{ marginRight: '10px' }}>
        {customList(left, CUSTOMTRANSFERLIST.LEFTBOX, AVAILABLE_MODULE_TEXT, leftTitle, false)}
      </Grid>

      <Grid id={`${labelId}_right`} className={rightPanel} item>
        {customList(right, CUSTOMTRANSFERLIST.RIGHTBOX, isRealmEntitlement ? SUBSCRIBED_MODULES : SELECTED_MODULE_TEXT, rightTitle, true)}
      </Grid>
    </Grid>
  );
};

export default CustomTransferList;
